import { supabase } from '../supabase/config';

export const showFullScreenImage = (imageUrl, userName) => {
  window.dispatchEvent(new CustomEvent('openFullScreenImage', {
    detail: { imageUrl, userName }
  }));
};

export const closeFullScreenImage = () => {
  window.dispatchEvent(new CustomEvent('closeFullScreenImage'));
};

export const showPatchNotesModal = () => {
  window.dispatchEvent(new CustomEvent('openPatchNotesModal'));
};

export const showSupportModal = () => {
  window.dispatchEvent(new CustomEvent('openSupportModal'));
};

// Logout confirmation modal
export const showLogoutConfirm = () => {
  window.dispatchEvent(new CustomEvent('openConfirmModal', {
    detail: {
      icon: '👋',
      iconBg: '#fff3e0',
      title: 'Logout',
      message: 'Are you sure you want to logout?',
      confirmText: 'Logout',
      cancelText: 'Stay',
      confirmColor: '#2c2c2c',
      confirmHoverColor: '#444444',
      onConfirm: () => {
        if (window.performLogout) window.performLogout();
      }
    }
  }));
};

export const showDeleteAccountModal = () => {
  const currentUser = window.currentAppUser || window.currentUser;
  if (!currentUser) return;

  window.dispatchEvent(new CustomEvent('openConfirmModal', {
    detail: {
      icon: '⚠️',
      iconBg: '#fef7f7',
      title: 'Delete Account',
      message: 'This will permanently delete your account, all your events, and all your data. This action cannot be undone.',
      confirmText: 'Delete My Account',
      cancelText: 'Keep Account',
      confirmColor: '#ea4335',
      confirmHoverColor: '#d33426',
      onConfirm: async () => {
        if (window.confirmDeleteAccount) await window.confirmDeleteAccount();
      }
    }
  }));
};

// Confirm and execute account deletion
export const confirmDeleteAccount = async () => {
  const currentUser = window.currentAppUser || window.currentUser;
  if (!currentUser) return;
  
  const userId = currentUser.uid;
  const supabaseClient = window.supabase || supabase;
  
  try {
    // Disable buttons
    const deleteBtn = document.getElementById('confirm-delete-account-btn');
    const cancelBtn = document.getElementById('cancel-delete-account-btn');
    
    deleteBtn.disabled = true;
    deleteBtn.textContent = 'Deleting...';
    deleteBtn.style.cursor = 'not-allowed';
    deleteBtn.style.opacity = '0.7';
    
    cancelBtn.disabled = true;
    cancelBtn.style.cursor = 'not-allowed';
    cancelBtn.style.opacity = '0.5';
    
    // STEP 1: Get all events created by this user
    const { data: createdEvents, error: eventsError } = await supabaseClient
      .from('events')
      .select('event_id, title, participants')
      .eq('creator_id', userId);
    
    if (eventsError) throw eventsError;
    
// STEP 2: Delete events and remove markers locally (no full refresh)
    if (createdEvents && createdEvents.length > 0) {
      const allNotifications = [];
      const deletedEventIds = [];
      
      for (const event of createdEvents) {
        const affectedParticipants = event.participants?.filter(p => p !== userId) || [];
        
        if (affectedParticipants.length > 0) {
          const notifications = affectedParticipants.map(participantId => ({
            user_id: participantId,
            type: 'event_deleted',
            title: 'Event Removed',
            message: `The event "${event.title}" has been cancelled.`,
            event_id: event.event_id,
            event_title: event.title,
            is_read: false
          }));
          
          allNotifications.push(...notifications);
        }
        
        // Delete the event from database
        await supabaseClient
          .from('events')
          .delete()
          .eq('event_id', event.event_id);
        
        deletedEventIds.push(event.event_id);
      }
      
      // Remove ONLY these specific markers from the map (no full refresh)
      if (window.removeEventMarkers && deletedEventIds.length > 0) {
        window.removeEventMarkers(deletedEventIds);
      }
      
      // Schedule notifications for 1 minute later (after markers are removed)
      if (allNotifications.length > 0) {
        // Store in a global variable so it persists after sign out
        window.pendingDeletionNotifications = {
          notifications: allNotifications,
          supabaseUrl: supabaseClient.supabaseUrl,
          anonKey: supabaseClient.supabaseKey
        };
      }
    }
    
// STEP 3: Remove user from ALL events (comprehensive cleanup)
    // Get ALL events (we'll filter in JavaScript for reliability)
    const { data: allEvents, error: allEventsError } = await supabaseClient
      .from('events')
      .select('event_id, participants, pending_requests, creator_id');
    
    if (allEventsError) throw allEventsError;
    
    if (allEvents && allEvents.length > 0) {
      let updateCount = 0;
      
      for (const event of allEvents) {
        // Skip if user is the creator (already handled in Step 2)
        if (event.creator_id === userId) continue;
        
        const wasParticipant = event.participants?.includes(userId);
        const wasPending = event.pending_requests?.includes(userId);
        
        if (wasParticipant || wasPending) {
          // Remove user from both arrays
          const updatedParticipants = (event.participants || []).filter(p => p !== userId);
          const updatedPendingRequests = (event.pending_requests || []).filter(p => p !== userId);
          
          const { error: updateError } = await supabaseClient
            .from('events')
            .update({ 
              participants: updatedParticipants,
              pending_requests: updatedPendingRequests
            })
            .eq('event_id', event.event_id);
          
          if (updateError) {
            console.error(`Error updating event ${event.event_id}:`, updateError);
          } else {
            updateCount++;
          }
        }
      }
    }
    
    // STEP 4: Delete all user's reports (both as reporter and reported)
    await supabaseClient
      .from('reports')
      .delete()
      .or(`reporter_id.eq.${userId},reported_user_id.eq.${userId}`);
    
    // STEP 5: Delete all user's notifications
    await supabaseClient
      .from('notifications')
      .delete()
      .eq('user_id', userId);
    
    // STEP 6: Delete user completely (profile + auth)
    const { error: deleteError } = await supabaseClient.rpc('delete_user_account', {
      user_id_to_delete: userId
    });
    
    if (deleteError) {
      // Fallback: just delete from users table
      await supabaseClient
        .from('users')
        .delete()
        .eq('id', userId);
    }
    
// STEP 8: Close all panels before signing out
    if (typeof window.closeAllPanels === 'function') {
      window.closeAllPanels();
    }
    
    // Also remove any other panels that might be open
    const eventDetailsPanel = document.getElementById('eventDetailsPanel');
    if (eventDetailsPanel) eventDetailsPanel.remove();
    
    const chatPanel = document.getElementById('chatPanel');
    if (chatPanel) chatPanel.remove();
    
    const profilePanel = document.getElementById('profilePanel');
    if (profilePanel) profilePanel.remove();
    
    const chatsListPanel = document.getElementById('chatsListPanel');
    if (chatsListPanel) chatsListPanel.remove();
    
    // Sign out user
    await supabaseClient.auth.signOut();
    
    // Close modal
    const deleteModal = document.getElementById('deleteAccountModal');
    if (deleteModal) deleteModal.remove();
    
    // Show success message
    window.showToast('Your account has been permanently deleted.', 'success');
    
    // Set up delayed notification sending (will execute after redirect)
    if (window.pendingDeletionNotifications) {
      setTimeout(async () => {
        try {
          const { notifications, supabaseUrl, anonKey } = window.pendingDeletionNotifications;
          
          // Create a new Supabase client (since user is signed out)
          const { createClient } = await import('@supabase/supabase-js');
          const tempClient = createClient(supabaseUrl, anonKey);
          
          await tempClient
            .from('notifications')
            .insert(notifications);
          // Clean up
          delete window.pendingDeletionNotifications;
        } catch (err) {
          console.error('Error sending delayed notifications:', err);
        }
      }, 60000); // 1 minute
    }
  } catch (error) {
    window.showToast('Failed to delete account. Please try again or contact support.', 'error');
    
    // Re-enable buttons
    const deleteBtn = document.getElementById('confirm-delete-account-btn');
    const cancelBtn = document.getElementById('cancel-delete-account-btn');
     
    if (deleteBtn) {
      deleteBtn.disabled = false;
      deleteBtn.textContent = 'Delete My Account';
      deleteBtn.style.cursor = 'pointer';
      deleteBtn.style.opacity = '1';
    }
    
    if (cancelBtn) {
      cancelBtn.disabled = false;
      cancelBtn.style.cursor = 'pointer';
      cancelBtn.style.opacity = '1';
    }
  }
};

window.showFullScreenImage = showFullScreenImage;
window.closeFullScreenImage = closeFullScreenImage;
window.showPatchNotesModal = showPatchNotesModal;
window.showSupportModal = showSupportModal;
window.showLogoutConfirm = showLogoutConfirm;
window.showDeleteAccountModal = showDeleteAccountModal;
window.confirmDeleteAccount = confirmDeleteAccount;